import { Link as RouterLink } from 'react-router-dom';

import env from 'environments';
import { Link } from 'modules/v2/draft/components';
import { useGhlWebsiteState } from 'modules/v2/draft/hooks/useGhlWebsite';
import { normalizeUrl } from 'modules/v2/utils/stringManipulation';

const LEGACY_PREVIEW_PATTERN = 'gohighlevel.com/v2/preview';

/**
 * Body of the "Lead capture site (Marketing Hub)" row on the draft page.
 *
 * DS creates the GHL website record from the book builder's site-ready Kafka
 * event, after the draft's site is published. Polling is gated on
 * `gateReady` (the publish saga has finished) and bounded (backoff, 2-minute
 * budget). A 404 ends it at once unless this session just published
 * (`expectRecord`), when the record lags a few seconds behind the consumer.
 *
 * States: the link when DS has a URL; a pulsing placeholder while an answer
 * is still expected; otherwise a static empty state with the Marketing Hub
 * entry point.
 */
const MarketingHubLink = ({ draftId, gateReady, expectRecord }) => {
  const { website, status } = useGhlWebsiteState(draftId, { gateReady, expectRecord });

  // The legacy shared GHL preview URL is never exposed: it is owned by a
  // single sub-account and would re-leak form submissions across tenants.
  const ghlBookPageUrl = website?.ghlBookPageUrl;
  const isLegacyPreviewUrl =
    typeof ghlBookPageUrl === 'string' && ghlBookPageUrl.includes(LEGACY_PREVIEW_PATTERN);
  const isReady = Boolean(ghlBookPageUrl && !isLegacyPreviewUrl);

  if (isReady) {
    return <Link key="marketingHub" id="marketingHub" href={normalizeUrl(ghlBookPageUrl)} />;
  }

  if (status === 'loading') {
    return (
      <div
        className="mt-2 h-[40px] rounded-md border border-[#e9e9e9] bg-neutral-100 animate-pulse flex items-center px-3"
        role="status"
        aria-live="polite"
        aria-label="Preparing your Marketing Hub link"
      >
        <span className="text-sm text-neutral-400">Preparing your Marketing Hub link…</span>
      </div>
    );
  }

  return (
    <div
      className="mt-2 h-[40px] rounded-md border border-[#e9e9e9] bg-neutral-100 flex items-center justify-between gap-2 px-3"
      data-testid="marketing-hub-empty"
    >
      <span className="text-sm text-neutral-500">
        {status === 'not-found'
          ? 'Not set up for this book yet'
          : 'Marketing Hub link not available yet'}
      </span>
      {env.GHL_ENABLED && (
        <RouterLink
          to="/marketing-hub"
          className="text-sm font-semibold text-neutral-800 underline whitespace-nowrap"
        >
          Open Marketing Hub
        </RouterLink>
      )}
    </div>
  );
};

export default MarketingHubLink;
